import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Bulk action: assign N selected gates to an existing group, create
 * a new group on the fly, or detach them from any group. Used from
 * the bulk-actions menu on the map and from the gates table.
 */
function AssignToGroupDialog({
  siteId,
  gateIds,
  apiBase = 'customer',
  onClose,
  onApplied,
}) {
  const [groups, setGroups] = useState([]);
  const [choice, setChoice] = useState(null);     // group id, "new", or "none"
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`/api/${apiBase}/sites/${siteId}/gate-groups`)
      .then(res => setGroups(res.data))
      .catch(() => setError('שגיאה בטעינת הקבוצות'));
  }, [siteId, apiBase]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      let target = null;
      if (choice === 'new') {
        if (!newName.trim()) {
          setError('נא להזין שם לקבוצה החדשה');
          setSaving(false);
          return;
        }
        const res = await axios.post(`/api/${apiBase}/sites/${siteId}/gate-groups`, {
          name: newName.trim(),
        });
        target = res.data.id;
      } else if (choice === 'none') {
        target = null;
      } else if (typeof choice === 'number') {
        target = choice;
      } else {
        setError('יש לבחור אופציה');
        setSaving(false);
        return;
      }
      await axios.put(`/api/${apiBase}/gates/group`, { gate_ids: gateIds, group_id: target });
      onApplied && onApplied();
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בשיוך');
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 110,
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        borderRadius: '10px',
        padding: '1.25rem',
        width: 'min(420px, calc(100vw - 24px))',
        maxHeight: '85vh',
        overflowY: 'auto',
        zIndex: 111,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: 0, marginBottom: '0.4rem', fontSize: '1.05rem', fontWeight: 600 }}>
          שייך {gateIds.length} שערים לקבוצה
        </h3>
        <p style={{ margin: 0, marginBottom: '1.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
          השערים יירשו את הגדרות הקבוצה אלא אם הוגדרו להם הגדרות פרטיות.
        </p>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {groups.map(g => (
            <li key={g.id} style={{ marginBottom: '0.4rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.55rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                background: choice === g.id ? '#f3f4f6' : '#fff',
              }}>
                <input
                  type="radio"
                  name="group"
                  checked={choice === g.id}
                  onChange={() => { setChoice(g.id); setError(null); }}
                />
                <span style={{ fontSize: '0.9rem', flex: 1 }}>{g.name}</span>
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{g.gate_count} שערים</span>
              </label>
            </li>
          ))}
          <li style={{ marginBottom: '0.4rem' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.55rem 0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              background: choice === 'new' ? '#f3f4f6' : '#fff',
            }}>
              <input
                type="radio"
                name="group"
                checked={choice === 'new'}
                onChange={() => { setChoice('new'); setError(null); }}
              />
              <span style={{ fontSize: '0.9rem' }}>צור קבוצה חדשה:</span>
              <input
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setChoice('new'); }}
                placeholder="שם הקבוצה"
                style={{
                  flex: 1, padding: '0.3rem 0.5rem',
                  border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem',
                }}
              />
            </label>
          </li>
          <li>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.55rem 0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              background: choice === 'none' ? '#f3f4f6' : '#fff',
            }}>
              <input
                type="radio"
                name="group"
                checked={choice === 'none'}
                onChange={() => { setChoice('none'); setError(null); }}
              />
              <span style={{ fontSize: '0.9rem' }}>נתק מקבוצה</span>
            </label>
          </li>
        </ul>

        {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: '0.45rem 0.9rem', background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer' }}
          >
            ביטול
          </button>
          <button
            onClick={submit}
            disabled={saving || choice === null}
            style={{
              padding: '0.45rem 1.1rem',
              background: '#1f2937',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              fontSize: '0.85rem',
              cursor: saving ? 'wait' : 'pointer',
              opacity: (saving || choice === null) ? 0.7 : 1,
            }}
          >
            {saving ? 'משייך...' : 'שייך'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

export default AssignToGroupDialog;
